import React, { useEffect, useCallback, useRef, useState } from 'react';
import { CMxComponents } from '@codametrix/ui-components/src/types';
import { ModalComponent } from '../Modal/modal';
import { Button } from '../button/button';
import { modalSubmit } from '../../actions/ui';
import { useDispatch } from 'react-redux';
import { Action } from 'redux';

export type modalState = {
  timeLeft: number;
  countdownSet: number | null;
};

const defaultForceSubmitOptions = {
  isForced: false,
  time: 60,
  message: 'Submitting Automatically in '
};

export const LogoutModal: React.FC<CMxComponents.ModalProps> = ({
  buttonText,
  children,
  footerChildren,
  handler,
  icon,
  modalParams,
  title,
  closable = true,
  forceSubmitOptions = defaultForceSubmitOptions,
  ...rest
}: CMxComponents.ModalProps) => {

  const dispatch = useDispatch();

  const [modalState, setModalState] = useState<modalState>({
    timeLeft: forceSubmitOptions?.time ?? 60,
    countdownSet: null
  });
  const [isModalOpen, setIsModalOpen] = useState<boolean>(true);
  const hasForcedSubmitRef = useRef(false);

  useEffect(() => {
    if (!forceSubmitOptions?.isForced) return;
    const myInterval = setInterval(() => {
      // State updaters run during React's state-transition phase and must stay pure, so
      // this only decrements. The submit is dispatched by the effect below once the
      // countdown reaches zero — dispatching from inside the updater triggered
      // "Cannot update during an existing state transition" and, because the interval
      // kept ticking past zero, re-dispatched the logout every second.
      setModalState(prev =>
        prev.timeLeft <= 0 || prev.countdownSet !== null
          ? prev
          : { ...prev, timeLeft: prev.timeLeft - 1 }
      );
    }, 1000);
    return () => {
      clearInterval(myInterval);
    };
  }, [forceSubmitOptions?.isForced]);

  // Fire the forced submit exactly once, after the countdown reaches zero. The ref guard
  // is load-bearing: modalParams is typically a fresh object per render, so these deps
  // churn even once the countdown has finished.
  useEffect(() => {
    if (!forceSubmitOptions?.isForced) return;
    if (modalState.timeLeft > 0) return;
    if (hasForcedSubmitRef.current) return;
    hasForcedSubmitRef.current = true;
    if (handler) {
      dispatch(
        (modalSubmit({ name: handler, params: modalParams }) as any) as Action
      );
    }
  }, [
    forceSubmitOptions?.isForced,
    modalState.timeLeft,
    handler,
    modalParams,
    dispatch
  ]);

  const _handleClick = async (
    event: React.MouseEvent | React.FormEvent | CustomEvent
  ) => {
    event.preventDefault();
    if (handler) {
      dispatch(
        (modalSubmit({ name: handler, params: modalParams }) as any) as Action
      );
    }
  };

  const forcedSubmitMessage =
    forceSubmitOptions && forceSubmitOptions?.message + modalState.timeLeft;

  const handleUnload = useCallback(
    (event: Event) => {
      if (handler) {
        dispatch(
          (modalSubmit({ name: handler, params: modalParams }) as any) as Action
        );
      }
    },
    [handler, dispatch, modalParams]
  );

  useEffect(() => {
    window.addEventListener('unload', handleUnload);
    window.addEventListener('popstate', handleUnload);

    return () => {
      window.removeEventListener('unload', handleUnload);
      window.removeEventListener('popstate', handleUnload);
    };
  }, [handleUnload]);

  return (
    <ModalComponent
      size={'lg'}
      isOpen={isModalOpen}
      handleClose={() => {
        setIsModalOpen(false);
      }}
      showCloseIcon={closable}
      modalHeader={
        <div>
          {icon && <span>{icon}</span>}
          {title && <div>{title}</div>}
        </div>
      }
      modalContent={
        <div>
          {forceSubmitOptions?.isForced ? forcedSubmitMessage : children}
        </div>
      }
      modalFooter={
        <div onClick={_handleClick}>
          <Button dataTestId="submitButton" label={buttonText ?? ''} />{' '}
          {footerChildren && <div>{footerChildren} </div>}
        </div>
      }
    />
  );
};
